import { Badge, Card, Form, Stack } from 'react-bootstrap';
import Loading from '../components/loading';
import React, { useEffect, useState } from 'react';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import 'dayjs/locale/pt-br';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SuccessMessage from '../components/successMessage';
import ErrorMessage from '../components/errorMessage';
import { Ban, CalendarCheck, Check } from 'lucide-react';
import {
  Autocomplete,
  createFilterOptions,
  Box,
  Menu,
  ListItemIcon,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogTitle,
  DialogContent,
  Paper,
  Link,
} from '@mui/material';
import { WhatsApp } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ProfessionalModal from '../register/professional/professionalModal';
import ConsultationTypeFormModal from '../register/consultationTypes/consultationTypeFormModal';
import CancelAppointmentDialog from './cancelAppointmentDialog';
import AppointmentHistoryModal from './appointmentHistoryModal';
import { useAppointment } from './hooks/useAppointment';
import { useSaveAppointment } from './hooks/useSaveAppointment';
import { useCheckProfessionalData } from './hooks/useCheckProfessionalData';
import { useAppointmentActions } from './hooks/useAppointmentActions';
import { usePatientContact } from './hooks/usePatientContact';
import { usePatientsComboBox } from '../hooks/usePatientsComboBox';
import { useProfessionals } from '../hooks/useProfessionals';
import { useProfessionalOptions, professionalOptionLabel } from './hooks/useProfessionalOptions';
import { useAppointmentTypes } from '../hooks/useAppointmentTypes';
import { useRooms } from '../hooks/useRooms';
import { useRoomLabel } from '../hooks/useRoomLabel';

const PROFESSIONAL_STORAGE_KEY = 'lastSelectedProfessionalId';
const ROOM_STORAGE_KEY = 'lastSelectedRoomId';

function Appointment() {
  const navigate = useNavigate();

  const { appointmentId } = useParams();

  const location = useLocation();
  const { appointmentData } = location.state || {};

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedPatient, setSelectedPatient] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  const [showModalToCancelAppointment, setShowModalToDeleteAppointment] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showModalToNoShow, setShowModalToNoShow] = useState(false);
  const [showModalToPerform, setShowModalToPerform] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [patientPhoneNumber, setPatientPhoneNumber] = useState('');

  const [selectedProfessionalId, setSelectedProfessionalId] = useState('');
  const [isDentistModalOpen, setDentistModalOpen] = useState(false);
  const [isAppointmentTypeModalOpen, setAppointmentTypeModalOpen] = useState(false);
  const [isTypeSelectOpen, setTypeSelectOpen] = useState(false);

  const [selectedRoomId, setSelectedRoomId] = useState(0);

  const [showProfessionalWarningDialog, setShowProfessionalWarningDialog] = useState(false);
  const [professionalWarningMessage, setProfessionalWarningMessage] = useState('');

  const [appointmentDate, setAppointmentDate] = useState(dayjs());
  const [appointmentHour, setAppointmentHour] = useState(dayjs());
  const [appointmentEndHour, setAppointmentEndHour] = useState(null);
  const [appointmentObservation, setAppointmentObservation] = useState('');
  const [appointmentType, setAppointmentType] = useState('');
  const [appointmentStatus, setAppointmentStatus] = useState('');
  const [appointmentStatusDescription, setAppointmentStatusDescription] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const canReactivate = ['CANCELED', 'PERFORMED', 'NO_SHOW'].includes(appointmentStatus);

  const { patients: comboBoxPatients } = usePatientsComboBox();
  const { professionals: comboBoxDentists, refetch: refetchDentists } = useProfessionals();
  const { appointmentTypes, refetch: refetchAppointmentTypes } = useAppointmentTypes();
  const { rooms } = useRooms();
  const roomLabel = useRoomLabel();
  const {
    appointment,
    isLoading: isAppointmentLoading,
    isStale: isAppointmentStale,
    updatedAt: appointmentUpdatedAt,
    refetch: refetchAppointment,
  } = useAppointment(appointmentId);
  const dentistOptions = useProfessionalOptions(comboBoxDentists, appointment);
  const { save: saveAppointmentRequest } = useSaveAppointment();
  const { check: checkProfessionalData } = useCheckProfessionalData();
  const { confirm, perform, cancel, noShow, remove, reactivate } = useAppointmentActions();
  const { fetchContact } = usePatientContact();

  const filter = createFilterOptions({
    stringify: (option) => option.label,
  });

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const openModalToCancelAppointment = () => {
    setShowModalToDeleteAppointment(true);
    closeMenu();
  };

  const closeModalToCancelAppointment = () => {
    setShowModalToDeleteAppointment(false);
    setSelectedPatient(null);
  };

  useEffect(() => {
    const professionalIdFromUser = sessionStorage.getItem('dentistIdFromUser');
    if (
      professionalIdFromUser &&
      professionalIdFromUser !== 'null' &&
      professionalIdFromUser !== 'undefined'
    ) {
      setSelectedProfessionalId(professionalIdFromUser);
    }

    if (!appointmentId) {
      setAppointmentDate(dayjs(appointmentData?.date));
      setAppointmentHour(dayjs(appointmentData?.hour, 'HH:mm'));
      setSelectedRoomId(appointmentData?.roomId ?? '');
    }
    console.log('select:', selectedRoomId);
  }, [appointmentData, appointmentId]);

  // Pré-preenche profissional e sala com a última seleção salva ao criar uma
  // consulta (inclusive vindo do calendário). Só preenche o que estiver vazio:
  // o profissional do usuário logado e a sala vinda do calendário têm
  // prioridade. Os efeitos dependem das listas porque o id salvo é string e
  // precisa ser resolvido (e validado) contra as opções carregadas.
  useEffect(() => {
    if (appointmentId) {
      return;
    }
    const savedProfessionalId = localStorage.getItem(PROFESSIONAL_STORAGE_KEY);
    const professional = comboBoxDentists.find((d) => String(d.id) === savedProfessionalId);
    if (professional) {
      setSelectedProfessionalId((current) => current || professional.id);
    }
  }, [appointmentId, comboBoxDentists]);

  useEffect(() => {
    if (appointmentId) {
      return;
    }
    const savedRoomId = localStorage.getItem(ROOM_STORAGE_KEY);
    const room = rooms.find((r) => String(r.id) === savedRoomId);
    if (room) {
      setSelectedRoomId((current) => current || room.id);
    }
  }, [appointmentId, rooms]);

  useEffect(() => {
    if (!appointment) {
      return;
    }
    setSelectedPatient({ id: appointment.pacienteId, label: appointment.nomeDoPaciente });
    setPatientPhoneNumber(appointment.whatsappDoPaciente);
    setSelectedProfessionalId(appointment.dentistaId);
    setSelectedRoomId(appointment.roomId ?? '');
    setAppointmentDate(dayjs(appointment.data, 'DD/MM/YYYY'));
    setAppointmentHour(dayjs(appointment.hora, 'HH:mm'));
    setAppointmentEndHour(dayjs(appointment.hora, 'HH:mm').add(appointment.duracao, 'minute'));
    setAppointmentObservation(appointment.observacao);
    setAppointmentType(appointment.appointmentTypeId ?? '');
    setAppointmentStatusDescription(appointment.descricaoDoStatus);
    setAppointmentStatus(appointment.status);
    setCancellationReason(appointment.cancellationReason ?? '');
  }, [appointment]);

  const buildAppointment = () => ({
    pacienteId: selectedPatient.id,
    dentistaId: selectedProfessionalId,
    roomId: selectedRoomId,
    data: dayjs(appointmentDate).format('DD/MM/YYYY'),
    hora: dayjs(appointmentHour).format('HH:mm'),
    horaFim: dayjs(appointmentEndHour).format('HH:mm'),
    duracao: dayjs(appointmentEndHour).diff(dayjs(appointmentHour), 'minute'),
    observacao: appointmentObservation,
    appointmentTypeId: appointmentType,
    id: appointmentId,
    telefone: patientPhoneNumber,
  });

  const saveAppointment = (event) => {
    event.preventDefault();
    setLoading(true);

    if (!validateRequiredFields()) {
      return;
    }

    const appointment = buildAppointment();

    checkProfessionalData(appointment)
      .then((returnFromApi) => {
        const warningMessage = returnFromApi.data?.message;
        if (warningMessage) {
          setLoading(false);
          setProfessionalWarningMessage(warningMessage);
          setShowProfessionalWarningDialog(true);
        } else {
          persistAppointment(appointment);
        }
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  const persistAppointment = (appointment) => {
    setLoading(true);

    if (appointmentId) {
      saveAppointmentRequest(appointment)
        .then((returnFromApi) => {
          setLoading(false);
          setShowSuccessMessage(true);
          setSuccessMessage(returnFromApi.data);
        })
        .catch((returnFromApi) => {
          setLoading(false);
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data);
          console.error(returnFromApi);
        });
    } else {
      saveAppointmentRequest(appointment)
        .then((returnFromApi) => {
          setLoading(false);
          setShowSuccessMessage(true);
          setSuccessMessage(returnFromApi.data.message);
          navigate(`/appointment/${returnFromApi.data.appointmentId}`);
        })
        .catch((returnFromApi) => {
          setLoading(false);
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data);
          console.error(returnFromApi);
        });
    }
  };

  const confirmProfessionalWarning = () => {
    setShowProfessionalWarningDialog(false);
    persistAppointment(buildAppointment());
  };

  const closeProfessionalWarning = () => {
    setShowProfessionalWarningDialog(false);
    setProfessionalWarningMessage('');
  };

  function validateRequiredFields() {
    if (
      !selectedPatient ||
      !selectedProfessionalId ||
      !appointmentType ||
      !appointmentEndHour ||
      !appointmentHour ||
      !appointmentDate
    ) {
      setShowErrorMessage(true);
      setErrorMessage(
        'Para salvar uma consulta é obrigatório preencher os campos: Paciente, Profissional, Data, Hora de Início, Hora de Fim e Tipo',
      );
      setLoading(false);
      return false;
    }
    return true;
  }

  function reactivateAppointment() {
    if (!appointmentId) {
      setShowErrorMessage(true);
      setErrorMessage('Salve a consulta antes de reativá-la.');
      return;
    }
    setLoading(true);
    reactivate(appointmentId)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        refetchAppointment();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      });
  }

  function confirmAppointment() {
    closeMenu();
    setLoading(true);
    confirm(appointmentId)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        refetchAppointment();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      });
  }

  function performAppointment() {
    setLoading(true);
    perform(appointmentId)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        refetchAppointment();
        setShowModalToPerform(false);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  }

  function noShowAppointment() {
    setLoading(true);
    noShow(appointmentId)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        refetchAppointment();
        setShowModalToNoShow(false);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      });
  }

  function cancelAppointment(reason) {
    setLoading(true);
    cancel(appointmentId, reason)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        refetchAppointment();
        closeModalToCancelAppointment();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      });
  }

  function deleteAppointment() {
    setShowDeleteDialog(false);
    setLoading(true);
    remove(appointmentId)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        cleanFields();
        navigate('/appointment');
      })
      .catch((err) => {
        setLoading(false);
        setShowDeleteDialog(false);
        setShowErrorMessage(true);
        setErrorMessage(err.response?.data ?? 'Erro ao excluir consulta.');
        console.error(err);
      });
  }

  function cleanFields() {
    const savedProfessionalId = localStorage.getItem(PROFESSIONAL_STORAGE_KEY);
    const savedRoomId = localStorage.getItem(ROOM_STORAGE_KEY);
    setSelectedProfessionalId(
      comboBoxDentists.find((d) => String(d.id) === savedProfessionalId)?.id ?? '',
    );
    setSelectedRoomId(rooms.find((room) => String(room.id) === savedRoomId)?.id ?? '');
    setSelectedPatient(null);
    setAppointmentDate(dayjs());
    setAppointmentHour(dayjs());
    setAppointmentEndHour(null);
    setAppointmentObservation('');
    setAppointmentType('');
    setPatientPhoneNumber('');
    setAppointmentStatus('');
  }

  function handleNewDentistSaved() {
    refetchDentists();
  }

  function handleNewAppointmentTypeClick() {
    setTypeSelectOpen(false);
    setAppointmentTypeModalOpen(true);
  }

  function handleNewAppointmentTypeSaved() {
    refetchAppointmentTypes();
  }

  function goToMedicalRecord() {
    if (!selectedPatient?.id) {
      setShowErrorMessage(true);
      setErrorMessage('Selecione um paciente para abrir o prontuário.');
      return;
    }
    navigate(`/patient-form/${selectedPatient.id}`);
  }

  function goToNewAppointment() {
    cleanFields();
    navigate('/appointment');
  }

  const whatsAppConfirm = () => {
    if (!selectedPatient || !selectedPatient.id) {
      setShowErrorMessage(true);
      setErrorMessage('Nenhum paciente selecionado.');
      return;
    }

    if (!selectedProfessionalId) {
      setShowErrorMessage(true);
      setErrorMessage('Nenhum profissional selecionado.');
      return;
    }

    const patientId = selectedPatient.id;

    const dentist = dentistOptions.find((d) => d.id === selectedProfessionalId);
    const dentistName = dentist ? dentist.nome : 'seu profissional';

    setLoading(true);

    fetchContact(patientId)
      .then((returnFromApi) => {
        setLoading(false);

        const patientPhone = returnFromApi.data.telefone;

        if (!patientPhone) {
          setShowErrorMessage(true);
          setErrorMessage('Telefone do paciente não encontrado. Verifique o cadastro do paciente.');
          return;
        }

        const phoneNumber = patientPhone.replace(/\D/g, '');
        if (phoneNumber.length < 10) {
          setShowErrorMessage(true);
          setErrorMessage('O número de telefone do paciente parece inválido.');
          return;
        }
        const fullPhoneNumber = `55${phoneNumber}`;

        const date = dayjs(appointmentDate).format('DD/MM/YYYY');
        const time = dayjs(appointmentHour).format('HH:mm');

        const message = `Olá, gostaríamos de confirmar sua consulta com a(o) Dra(o) ${dentistName} no dia ${date} às ${time}h. Por favor, responda "SIM" para confirmar.`;

        const encodedMessage = encodeURIComponent(message);

        window.open(
          `https://web.whatsapp.com/send/?phone=${fullPhoneNumber}&text=${encodedMessage}`,
          '_blank',
        );
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage('Erro ao buscar o telefone do paciente. ' + returnFromApi.message);
        console.error('Erro ao buscar paciente: ', returnFromApi);
      });
  };

  return (
    <Card className="rounded-4 shadow-lg border-0">
      <Card.Header className="border-0 py-3">
        <h5 className="mb-0 fw-bold text-muted">Consulta</h5>
      </Card.Header>

      <Loading isToHidden={isLoading === false && isAppointmentLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card.Body>
        {isAppointmentStale && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Sem conexão com o servidor. Exibindo os últimos dados salvos
            {appointmentUpdatedAt
              ? ` (${dayjs(appointmentUpdatedAt).format('DD/MM/YYYY HH:mm')})`
              : ''}
            . Alterações não podem ser salvas offline.
          </Alert>
        )}
        <Form onSubmit={saveAppointment}>
          <Box sx={{ width: '100%' }}>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item size={12}>
                <Stack direction="horizontal" gap={2}>
                  {appointmentStatus === 'SCHEDULED' && (
                    <Badge pill bg="info">
                      {appointmentStatusDescription}
                      <CalendarCheck size={15} />
                    </Badge>
                  )}
                  {appointmentStatus === 'CONFIRMED' && (
                    <Badge pill bg="primary" data-cy="appointment-status-badge">
                      {appointmentStatusDescription}
                      <Check size={15} />
                    </Badge>
                  )}
                  {appointmentStatus === 'CONFIRMED_BY_PATIENT' && (
                    <Badge pill bg="primary">
                      {appointmentStatusDescription}
                      <Check size={15} />
                    </Badge>
                  )}
                  {appointmentStatus === 'PERFORMED' && (
                    <Badge pill bg="success">
                      {appointmentStatusDescription}
                      <Check size={15} />
                    </Badge>
                  )}
                  {appointmentStatus === 'CANCELED' && (
                    <Badge pill bg="danger">
                      {appointmentStatusDescription}
                      <Ban size={15} />
                    </Badge>
                  )}
                  {appointmentId && (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      className="ms-auto"
                      data-cy="appointment-history-link"
                      onClick={() => setShowHistoryModal(true)}
                    >
                      Ver Histórico
                    </Link>
                  )}
                </Stack>
              </Grid>
            </Grid>
            {appointmentStatus === 'CANCELED' && cancellationReason && (
              <Alert severity="error" sx={{ mb: 2 }} data-cy="cancellation-reason">
                <strong>Motivo do cancelamento:</strong> {cancellationReason}
              </Alert>
            )}
            {appointment?.reminderSentAt && (
              <Alert severity="info" sx={{ mb: 2 }} data-cy="reminder-sent-at">
                <strong>Lembrete enviado ao paciente em:</strong> {appointment.reminderSentAt}
              </Alert>
            )}
            <FormControl fullWidth>
              <Autocomplete
                data-cy="patient-auto-complete"
                disablePortal
                options={comboBoxPatients}
                getOptionLabel={(option) => option.label ?? ''}
                filterOptions={filter}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Paciente" />}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
                value={selectedPatient}
                onChange={(_, newValue) => setSelectedPatient(newValue ?? null)}
                noOptionsText="Paciente não encontrado. Clique em Adicionar Paciente para fazer o cadastro."
                PaperComponent={({ children, ...other }) => (
                  <Paper {...other}>
                    {children}
                    <Box
                      sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Button fullWidth size="small" onClick={() => navigate('/patient-form')}>
                        + Adicionar Paciente
                      </Button>
                    </Box>
                  </Paper>
                )}
              />
            </FormControl>
            <Grid container spacing={2}>
              <Grid item size={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={dentistOptions}
                    getOptionLabel={professionalOptionLabel}
                    getOptionDisabled={(option) => Boolean(option.inactive)}
                    value={dentistOptions.find((d) => d.id === selectedProfessionalId) ?? null}
                    onChange={(_, newValue) => {
                      setSelectedProfessionalId(newValue?.id ?? '');
                      setSelectedRoomId(newValue?.roomId ?? '');
                      localStorage.setItem(PROFESSIONAL_STORAGE_KEY, newValue?.id ?? '');
                      localStorage.setItem(ROOM_STORAGE_KEY, newValue?.roomId ?? '');
                    }}
                    noOptionsText="Nenhum profissional encontrado."
                    PaperComponent={({ children, ...other }) => (
                      <Paper {...other}>
                        {children}
                        <Box
                          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <Button fullWidth size="small" onClick={() => setDentistModalOpen(true)}>
                            + Adicionar profissional
                          </Button>
                        </Box>
                      </Paper>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-cy="dentist-select"
                        fullWidth
                        label="Profissional"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
            {rooms.length > 1 && (
              <Grid container spacing={2}>
                <Grid item size={12}>
                  <FormControl fullWidth>
                    <InputLabel id="room-combobox-label">{roomLabel.singular}</InputLabel>
                    <Select
                      labelId="room-combobox-label"
                      id="room-combobox"
                      value={selectedRoomId}
                      label={roomLabel.singular}
                      onChange={(e) => {
                        setSelectedRoomId(e.target.value);
                        localStorage.setItem(ROOM_STORAGE_KEY, e.target.value);
                      }}
                    >
                      {rooms.map((room) => (
                        <MenuItem key={room.id} value={room.id}>
                          {room.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2}>
              <Grid item size={4}>
                <FormControl fullWidth>
                  <div data-cy="appointment-date">
                    <DatePicker
                      label="Data"
                      value={appointmentDate}
                      onChange={(newValue) => setAppointmentDate(newValue)}
                    />
                  </div>
                </FormControl>
              </Grid>
              <Grid item size={4}>
                <FormControl fullWidth>
                  <div data-cy="appointment-hour">
                    <TimeField
                      label="Hora de Início"
                      value={appointmentHour}
                      onChange={(newValue) => setAppointmentHour(newValue)}
                    />
                  </div>
                </FormControl>
              </Grid>
              <Grid item size={4}>
                <FormControl fullWidth>
                  <div data-cy="appointment-end-hour">
                    <TimeField
                      label="Hora de Fim"
                      value={appointmentEndHour}
                      onChange={(newValue) => setAppointmentEndHour(newValue)}
                    />
                  </div>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item size={12}>
                <FormControl fullWidth>
                  <TextField
                    id="outlined-multiline-static"
                    label="Observações"
                    multiline
                    rows={4}
                    value={appointmentObservation}
                    onChange={(e) => setAppointmentObservation(e.target.value)}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item size={12}>
                <FormControl fullWidth>
                  <InputLabel id="type-combobox-label">Tipo</InputLabel>
                  <Select
                    labelId="type-combobox-label"
                    id="type-combobox"
                    value={appointmentType}
                    label="Tipo"
                    MenuProps={{ MenuListProps: { sx: { pb: 0 } } }}
                    open={isTypeSelectOpen}
                    onOpen={() => setTypeSelectOpen(true)}
                    onClose={() => setTypeSelectOpen(false)}
                    onChange={(e) => setAppointmentType(e.target.value)}
                    SelectDisplayProps={{ 'data-cy': 'appointment-type' }}
                  >
                    <MenuItem value="">
                      <em>Selecione o tipo</em>
                    </MenuItem>
                    {appointmentTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.description}
                      </MenuItem>
                    ))}
                    <Box
                      sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Button fullWidth size="small" onClick={handleNewAppointmentTypeClick}>
                        + Novo tipo
                      </Button>
                    </Box>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box>
              <Button
                variant="outlined"
                onClick={() => goToMedicalRecord()}
                sx={{
                  top: '35px',
                }}
              >
                Prontuário
              </Button>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button variant="outlined" onClick={() => navigate('/agenda-router')}>
                    Voltar
                  </Button>

                  {appointmentId && (
                    <Button variant="outlined" onClick={goToNewAppointment}>
                      Nova Consulta
                    </Button>
                  )}

                  <Button variant="contained" type="submit">
                    Salvar
                  </Button>

                  {appointmentId && (
                    <>
                      <Button
                        aria-controls={anchorEl ? 'patient-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={anchorEl ? 'true' : undefined}
                        onClick={openMenu}
                        variant="outlined"
                        data-cy="appointment-actions-btn"
                      >
                        <MoreVertIcon />
                      </Button>
                      <Menu
                        id="patient-menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={closeMenu}
                      >
                        <MenuItem
                          data-cy="confirm-appointment-btn"
                          onClick={() => confirmAppointment()}
                        >
                          Confirmar Consulta
                        </MenuItem>

                        <MenuItem
                          data-cy="perform-appointment-btn"
                          onClick={() => {
                            setShowModalToPerform(true);
                            closeMenu();
                          }}
                        >
                          Marcar como Realizada
                        </MenuItem>

                        <MenuItem
                          data-cy="cancel-appointment-btn"
                          onClick={() => openModalToCancelAppointment()}
                        >
                          Cancelar Consulta
                        </MenuItem>

                        {canReactivate && (
                          <MenuItem
                            data-cy="reactivate-appointment-btn"
                            onClick={() => {
                              closeMenu();
                              reactivateAppointment();
                            }}
                          >
                            Reativar Consulta
                          </MenuItem>
                        )}

                        <MenuItem
                          data-cy="no-show-appointment-btn"
                          onClick={() => {
                            setShowModalToNoShow(true);
                            closeMenu();
                          }}
                        >
                          Informar Falta
                        </MenuItem>

                        <MenuItem
                          data-cy="delete-appointment-btn"
                          onClick={() => {
                            setShowDeleteDialog(true);
                            closeMenu();
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          Excluir Consulta
                        </MenuItem>

                        <MenuItem
                          onClick={() => {
                            whatsAppConfirm();
                          }}
                          sx={{ color: 'success.main' }}
                        >
                          <ListItemIcon>
                            <WhatsApp fontSize="small" sx={{ color: 'success.main' }} />
                          </ListItemIcon>
                          Enviar pelo WhatsApp
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Form>
        <Dialog
          open={showProfessionalWarningDialog}
          onClose={closeProfessionalWarning}
          aria-labelledby="professional-warning-dialog-title"
          aria-describedby="professional-warning-dialog-description"
        >
          <DialogTitle id="professional-warning-dialog-title">{'Confirmar consulta'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="professional-warning-dialog-description">
              {professionalWarningMessage}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeProfessionalWarning}>Cancelar</Button>
            <Button variant="contained" onClick={confirmProfessionalWarning}>
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>
        <CancelAppointmentDialog
          open={showModalToCancelAppointment}
          onClose={closeModalToCancelAppointment}
          onConfirm={cancelAppointment}
        />
        <Dialog
          open={showModalToPerform}
          onClose={() => setShowModalToPerform(false)}
          aria-labelledby="perform-dialog-title"
          aria-describedby="perform-dialog-description"
        >
          <DialogTitle id="perform-dialog-title">{'Marcar como Realizada'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="perform-dialog-description">
              Tem certeza de que deseja marcar esta consulta como realizada?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowModalToPerform(false)}>Não</Button>
            <Button data-cy="confirm-perform-appointment-btn" onClick={performAppointment}>
              Sim
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={showModalToNoShow}
          onClose={() => setShowModalToNoShow(false)}
          aria-labelledby="no-show-dialog-title"
          aria-describedby="no-show-dialog-description"
        >
          <DialogTitle id="no-show-dialog-title">{'Informar Falta'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="no-show-dialog-description">
              Tem certeza de que deseja informar falta para esta consulta?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowModalToNoShow(false)}>Não</Button>
            <Button
              data-cy="confirm-no-show-btn"
              onClick={noShowAppointment}
              sx={{ color: 'error.main' }}
            >
              Sim
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">{'Confirmar Exclusão'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Tem certeza de que deseja excluir esta consulta? Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>Não</Button>
            <Button
              data-cy="confirm-delete-appointment-btn"
              onClick={deleteAppointment}
              sx={{ color: 'error.main' }}
            >
              Sim
            </Button>
          </DialogActions>
        </Dialog>
        <AppointmentHistoryModal
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          appointmentId={appointmentId}
        />
        <ProfessionalModal
          open={isDentistModalOpen}
          onClose={() => setDentistModalOpen(false)}
          onSave={handleNewDentistSaved}
        />
        <ConsultationTypeFormModal
          open={isAppointmentTypeModalOpen}
          onClose={() => setAppointmentTypeModalOpen(false)}
          onSave={handleNewAppointmentTypeSaved}
        />
      </Card.Body>
    </Card>
  );
}

export default Appointment;
